import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Button, Modal } from 'tea-component';
import AnalyseForm from './components/AnalyseForm';
import AnalysePreview from './components/AnalysePreview';
import { getUrlCollectionDisplayImage, normalizeUrlCollectionSystemCode } from '@home/utils/urlCollectionData';
import { getUrlCollectionSystemDisplayInfo } from '../../utils/systemDisplay';

import type { IAnalysisUrlInfoResult } from '@home/enums/urlCollection';

import './index.scss';

interface IAnalyseUrlDataModalProps extends IBaseProps {
  visible: boolean;
  title?: string;
  url: string;
  pageName: string;
  analysisResult?: IAnalysisUrlInfoResult | null;
  importLoading?: boolean;
  confirmLoading?: boolean;
  shortcutImportErrorText?: string;
  onUrlChange?: (value: string) => void;
  onPageNameChange?: (value: string) => void;
  onImport?: () => void;
  onConfirm?: () => void;
  onClose?: () => void;
}

const AnalyseUrlDataModal: React.FC<IAnalyseUrlDataModalProps> = (props) => {
  const {
    style,
    className,
    visible,
    title,
    url,
    pageName,
    analysisResult,
    importLoading = false,
    confirmLoading = false,
    shortcutImportErrorText = '',
    onUrlChange,
    onPageNameChange,
    onImport,
    onConfirm,
    onClose,
  } = props;
  const { formatMessage } = useIntl();
  const modalTitle =
    title ||
    formatMessage({
      id: 'dbh_url_collection_analyse_modal_title',
      defaultMessage: 'Add to Shortcuts',
    });
  const urlLabel = formatMessage({
    id: 'dbh_url_collection_analyse_modal_url_label',
    defaultMessage: 'URL',
  });
  const urlPlaceholder = formatMessage({
    id: 'dbh_url_collection_analyse_modal_url_placeholder',
    defaultMessage: 'Please enter a DataBrain page URL',
  });
  const importButtonText = formatMessage({
    id: 'dbh_url_collection_analyse_modal_import',
    defaultMessage: 'Import',
  });
  const autoImportText = formatMessage({
    id: 'dbh_url_collection_analyse_modal_auto_import',
    defaultMessage: 'Auto Import',
  });
  const pageNameLabel = formatMessage({
    id: 'dbh_url_collection_analyse_modal_page_name_label',
    defaultMessage: 'Name',
  });
  const pageNamePlaceholder = formatMessage({
    id: 'dbh_url_collection_analyse_modal_page_name_placeholder',
    defaultMessage: 'You can rename the page after identification',
  });
  const confirmText = formatMessage({ id: 'dbh_confirm', defaultMessage: 'Confirm' });
  const cancelText = formatMessage({ id: 'dbh_cancel', defaultMessage: 'Cancel' });

  const trimmedUrl = url.trim();
  const systemCode = normalizeUrlCollectionSystemCode(
    analysisResult?.system || analysisResult?.page_path || analysisResult?.page_url || trimmedUrl || '',
  );
  const { systemAvatarIcon, systemName } = getUrlCollectionSystemDisplayInfo({
    system: systemCode,
    formatMessage,
    benchmarkMessage: {
      id: 'dbh_url_collection_analyse_modal_system_benchmark',
      defaultMessage: 'Benchmark',
    },
  });
  const trimmedPageName = pageName.trim();
  const previewTitle = trimmedPageName || analysisResult?.page_name || analysisResult?.page_path || '';
  const isEntityDetailPage = !!analysisResult?.entity_id && !!analysisResult?.entity_type?.length;
  const isSuccessResult = analysisResult?.success === true;
  const isFailureResult = analysisResult?.success === false;
  const previewImage = getUrlCollectionDisplayImage({
    entityType: isEntityDetailPage ? analysisResult?.entity_type || '' : '',
    cover: analysisResult?.entity_cover || '',
    systemIcon: systemAvatarIcon,
  });

  const isConfirmDisabled = !analysisResult?.success || !trimmedPageName || !!shortcutImportErrorText;
  const hasValidationError = !!shortcutImportErrorText;

  const illustrationFallbackText = (previewTitle || systemName || 'D').slice(0, 1).toUpperCase();
  const illustrationContent = importLoading ? null : isSuccessResult && analysisResult ? (
    previewImage ? (
      <img src={previewImage} alt={previewTitle} className="dbh-analyse-url-data-modal__illustration-cover" />
    ) : (
      <div className="dbh-analyse-url-data-modal__illustration-fallback">{illustrationFallbackText}</div>
    )
  ) : (
    <span className="dbh-analyse-url-data-modal__illustration-text">{autoImportText}</span>
  );
  const nameInputValue = isFailureResult ? '' : pageName;

  return (
    <Modal
      style={style}
      className={classnames(
        'dbh-my-shortcuts-and-new-features-card-common-modal dbh-analyse-url-data-modal',
        className,
      )}
      maskStyle={{ zIndex: 1001 }}
      caption={modalTitle}
      visible={visible}
      onClose={onClose}
    >
      <Modal.Body>
        <div className="dbh-analyse-url-data-modal__content">
          <div className="dbh-analyse-url-data-modal__hero">
            <AnalysePreview
              importLoading={importLoading}
              isSuccessResult={isSuccessResult}
              illustrationContent={illustrationContent}
            />
            <AnalyseForm
              url={url}
              importLoading={importLoading}
              hasValidationError={hasValidationError}
              trimmedUrl={trimmedUrl}
              urlLabel={urlLabel}
              urlPlaceholder={urlPlaceholder}
              importButtonText={importButtonText}
              pageNameLabel={pageNameLabel}
              pageNamePlaceholder={pageNamePlaceholder}
              nameInputValue={nameInputValue}
              onUrlChange={onUrlChange}
              onPageNameChange={onPageNameChange}
              onImport={onImport}
            />
          </div>
          {shortcutImportErrorText && (
            <div className="dbh-analyse-url-data-modal__hero-message">{shortcutImportErrorText}</div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" loading={confirmLoading} disabled={isConfirmDisabled} onClick={onConfirm}>
          {confirmText}
        </Button>
        <Button type="weak" onClick={onClose}>
          {cancelText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AnalyseUrlDataModal;
